// Floating layers: the one primitive under menus, lists, pickers and cards (D-062). A layer sits next to
// an element (or a point, for right-click menus), stays on screen, and closes on Escape and on any
// press outside itself. Layers opened from inside a layer (a submenu, a list in a picker) count as
// inside. Inside our modal Dialog the layer renders in the dialog, so it isn't blocked by it.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type Point = { x: number; y: number };
export type Anchor = HTMLElement | Point | null;
export type Placement = 'bottom-start' | 'bottom-end' | 'bottom' | 'top-start' | 'top' | 'right-start';
export type CloseReason = 'escape' | 'outside' | 'tab' | 'select' | 'focus';

interface Box { left: number; top: number; right: number; bottom: number }
const isPoint = (a: Anchor): a is Point => !!a && !(a instanceof Element) && typeof (a as Point).x === 'number';
const boxOf = (a: HTMLElement | Point): Box => {
  if (isPoint(a)) return { left: a.x, right: a.x, top: a.y, bottom: a.y };
  const r = a.getBoundingClientRect();
  return { left: r.left, right: r.right, top: r.top, bottom: r.bottom };
};

const EDGE = 8;
/** Where a w×h layer goes next to `b`, kept on screen; `maxHeight` when it can't fit. */
export function placeLayer(b: Box, w: number, h: number, placement: Placement, gap = 4, vw = innerWidth, vh = innerHeight) {
  if (placement === 'right-start') {
    let left = b.right + gap;
    if (left + w > vw - EDGE) left = Math.max(EDGE, b.left - w - gap);
    const maxHeight = vh - 2 * EDGE;
    const top = Math.max(EDGE, Math.min(b.top - 5, vh - Math.min(h, maxHeight) - EDGE));
    return { left, top, maxHeight };
  }
  const wantUp = placement.startsWith('top');
  const below = vh - b.bottom - gap - EDGE, above = b.top - gap - EDGE;
  let up: boolean;
  if (wantUp) up = h <= above || above >= below; else up = !(h <= below) && (h <= above || above > below);
  let maxHeight = Math.max(120, up ? above : below);
  const shown = Math.min(h, maxHeight);
  let top = up ? Math.max(EDGE, b.top - gap - shown) : b.bottom + gap;
  // A right-click menu (a point) taller than the room on either side slides up until it fits the window,
  // as native menus do, instead of opening downwards with a scroll bar (Slides' long menu, 5 Oct 2026).
  if (b.top === b.bottom && h > below && h > above && h <= vh - 2 * EDGE) { top = vh - h - EDGE; maxHeight = vh - 2 * EDGE; }
  let left = placement.endsWith('end') ? b.right - w : placement === 'bottom' || placement === 'top' ? (b.left + b.right) / 2 - w / 2 : b.left;
  // A menu that would run off the right edge opens to the left of the point instead, like a native one.
  if (left + w > vw - EDGE && placement.endsWith('start') && b.right - w >= EDGE) left = b.right - w;
  left = Math.max(EDGE, Math.min(left, vw - w - EDGE));
  return { left, top, maxHeight };
}

// The open layers, innermost last. Escape and outside presses go to the innermost one only.
const stack: { id: string; el: () => HTMLElement | null }[] = [];
export const isTopLayer = (id: string) => stack.length > 0 && stack[stack.length - 1].id === id;
/** Whether `node` is inside an open layer opened after the layer `id` (a submenu, a nested list). */
function inLaterLayer(id: string, node: Node) {
  const i = stack.findIndex((l) => l.id === id);
  return stack.slice(i + 1).some((l) => l.el()?.contains(node));
}

/** Where a layer for this element renders: in the open modal dialog that holds it, or the body. */
export function layerRoot(near?: Element | null): HTMLElement {
  const d = near?.closest?.('dialog[open]') as HTMLElement | null;
  if (d) return d;
  // A point anchor (right-click) inside a modal dialog: render in the topmost open modal.
  const modals = Array.from(document.querySelectorAll('dialog[open]')).filter((x) => { try { return x.matches(':modal'); } catch { return false; } });
  return (modals[modals.length - 1] as HTMLElement | undefined) ?? document.body;
}

const phone = () => typeof matchMedia !== 'undefined' && matchMedia('(max-width: 599px)').matches;
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface PopoverProps {
  open: boolean;
  onClose(reason: CloseReason): void;
  /** The element it belongs to, or a point (right-click). */
  anchor: Anchor;
  placement?: Placement;
  gap?: number;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** ARIA role of the layer: 'dialog' (default), 'menu', 'listbox' or 'presentation' when the content brings its own. */
  role?: string;
  label?: string;
  labelledBy?: string;
  id?: string;
  /** At least as wide as the anchor (lists under a field). */
  matchWidth?: boolean;
  /** Move focus in when it opens: true = first focusable or [data-autofocus]; 'container' = the layer itself; false = stay put. */
  focusOnOpen?: boolean | 'container';
  /** Put focus back on the anchor (or `returnTo`) when it closes from inside. */
  returnFocus?: boolean;
  returnTo?: HTMLElement | null;
  /** Close when focus moves out of it (Tab away). Default true. */
  closeOnFocusOut?: boolean;
  /** On phones (under 600 px wide), slide up from the bottom as a sheet. */
  sheet?: boolean;
  /** Where to look for an enclosing dialog when the anchor is a point. */
  within?: Element | null;
  testId?: string;
  onKeyDown?(e: React.KeyboardEvent<HTMLDivElement>): void;
}

/** A floating layer. Escape and any press outside it call `onClose`; it never saves anything itself. */
export function Popover({ open, ...rest }: PopoverProps) {
  if (!open) return null;
  return <Layer {...rest} />;
}

function Layer({ onClose, anchor, placement = 'bottom-start', gap = 4, children, className = '', style, role = 'dialog', label, labelledBy, id, matchWidth, focusOnOpen = true,
  returnFocus = true, returnTo, closeOnFocusOut = true, sheet, within, testId, onKeyDown }: Omit<PopoverProps, 'open'>) {
  const ref = useRef<HTMLDivElement>(null);
  const lid = useId();
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const asSheet = useState(() => !!sheet && phone())[0];
  const closeRef = useRef(onClose); closeRef.current = onClose;
  const anchorRef = useRef(anchor); anchorRef.current = anchor;
  const [root] = useState(() => layerRoot(anchor && !isPoint(anchor) ? anchor : within ?? (document.activeElement as Element | null)));
  const opener = useRef<Element | null>(typeof document !== 'undefined' ? document.activeElement : null);

  const place = useCallback(() => {
    const el = ref.current, a = anchorRef.current;
    if (!el || !a || asSheet) return;
    if (!isPoint(a) && !a.isConnected) return;
    const b = boxOf(a);
    const minW = matchWidth ? b.right - b.left : 0;
    const w = Math.max(el.offsetWidth, minW);
    const p = placeLayer(b, w, el.scrollHeight, placement, gap);
    setPos({ left: p.left, top: p.top, maxHeight: p.maxHeight, minWidth: minW || undefined });
  }, [placement, gap, matchWidth, asSheet]);

  useLayoutEffect(() => { place(); }, [place]);
  useEffect(() => {
    const entry = { id: lid, el: () => ref.current };
    stack.push(entry);
    let frame = 0;
    const later = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(place); };
    const onScroll = (e: Event) => { if (!ref.current?.contains(e.target as Node)) later(); };
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(later) : null;
    if (ref.current) ro?.observe(ref.current);
    const down = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      const a = anchorRef.current;
      if (a && !isPoint(a) && a.contains(t)) return; // the anchor toggles itself
      if (inLaterLayer(lid, t)) return;
      closeRef.current('outside');
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isTopLayer(lid) || e.isComposing) return;
      e.preventDefault(); e.stopPropagation(); // and the dialog under it stays open
      closeRef.current('escape');
    };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key, true);
    addEventListener('resize', later);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      const i = stack.indexOf(entry); if (i >= 0) stack.splice(i, 1);
      cancelAnimationFrame(frame); ro?.disconnect();
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('keydown', key, true);
      removeEventListener('resize', later);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [lid, place]);

  // Focus in on open; back to where it came from on close (only if focus was inside, so a click
  // elsewhere keeps its own focus).
  useEffect(() => {
    const el = ref.current;
    if (el && focusOnOpen) {
      // (Not hidden while it's being measured, so focus can go in at once.)
      const target = focusOnOpen === 'container' ? el : (el.querySelector<HTMLElement>('[data-autofocus]') ?? el.querySelector<HTMLElement>(FOCUSABLE) ?? el);
      target.focus({ preventScroll: true });
    }
    return () => {
      if (!returnFocus) return;
      const active = document.activeElement;
      const lost = !active || active === document.body || (el?.contains(active) ?? false) || !active.isConnected;
      const back = returnTo ?? (anchorRef.current && !isPoint(anchorRef.current) ? anchorRef.current : (opener.current as HTMLElement | null));
      // Checked a frame later: in React's development double-run the layer is still there.
      if (lost && back) setTimeout(() => {
        // Only if nothing else took focus meanwhile (a menu action that opened a dialog keeps it).
        const now = document.activeElement;
        if (now && now !== document.body && now.isConnected) return;
        if (!el?.isConnected && (back as HTMLElement).isConnected) (back as HTMLElement).focus({ preventScroll: true });
      }, 0);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onBlur = (e: React.FocusEvent) => {
    if (!closeOnFocusOut) return;
    const next = e.relatedTarget as Node | null;
    if (!next || ref.current?.contains(next) || inLaterLayer(lid, next)) return;
    const a = anchorRef.current;
    if (a && !isPoint(a) && a.contains(next)) return;
    closeRef.current('focus');
  };

  const node = (
    <div ref={ref} id={id} role={role === 'presentation' ? undefined : role} aria-label={label} aria-labelledby={labelledBy} data-testid={testId} tabIndex={-1}
      aria-modal={asSheet && role === 'dialog' ? true : undefined}
      className={`dz-layer${asSheet ? ' dz-layer--sheet' : ''} ${className}`} data-dz-layer=""
      style={asSheet ? style : { ...(pos ?? { left: 0, top: 0, opacity: 0, pointerEvents: 'none' }), ...style }}
      onBlur={onBlur} onKeyDown={onKeyDown}>
      {children}
    </div>
  );
  return createPortal(asSheet ? <><div className="dz-layer-scrim" aria-hidden onPointerDown={() => closeRef.current('outside')} />{node}</> : node, root);
}

/** Open/closed state plus the element it opens from, for the common "button opens a layer" case. */
export function useLayer<T extends HTMLElement = HTMLButtonElement>() {
  const [open, setOpen] = useState(false);
  const anchor = useRef<T>(null);
  return { open, setOpen, toggle: () => setOpen((o) => !o), close: () => setOpen(false), anchor };
}
